// src/pages/Schedule/ViewSchedulePage.jsx
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
    Badge,
    Box,
    Button,
    ButtonGroup,
    Divider,
    HStack,
    Heading,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Spinner,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    Wrap,
    WrapItem,
    useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";

import { fetchAllRoutes, fetchCouriersBySearch, getCalendarSchedule } from "../../api";

/**
 * Backend /calendar item shape (per your route):
 * {
 *   courierId, courierName, routeName, startTime, dayOff
 * }
 */

function pad2(n) {
    return String(n).padStart(2, "0");
}

function toISODateLocal(date) {
    // local date -> "YYYY-MM-DD"
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}-${m}-${d}`;
}

function parseISOToLocalDate(iso) {
    // "YYYY-MM-DD" -> local Date (no timezone shift)
    const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
    return new Date(y, (m || 1) - 1, d || 1);
}

function startOfWeekMonday(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay(); // 0 Sun .. 6 Sat
    const diff = day === 0 ? -6 : 1 - day; // Monday as first day
    d.setDate(d.getDate() + diff);
    return d;
}

function endOfWeekMonday(date) {
    const s = startOfWeekMonday(date);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return e;
}

function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function addMonths(date, months) {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
}

function getISODateRangeInclusive(start, end) {
    const out = [];
    let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
        out.push(toISODateLocal(cur));
        cur = addDays(cur, 1);
    }
    return out;
}

function formatRangeTitle(mode, selectedDate) {
    if (mode === "week") {
        const s = startOfWeekMonday(selectedDate);
        const e = endOfWeekMonday(selectedDate);
        return `${s.toLocaleDateString()} – ${e.toLocaleDateString()}`;
    }
    // month
    return selectedDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function normalizeCourierOptions(raw) {
    // supports: [{_id,name}] or [{value,label}] or mixed
    const arr = Array.isArray(raw) ? raw : [];
    return arr
        .map((c) => {
            if (!c) return null;
            if (c.value && c.label) return { value: String(c.value), label: String(c.label) };
            const id = c._id || c.id || c.value;
            const name = c.name || c.label;
            if (!id || !name) return null;
            return { value: String(id), label: String(name) };
        })
        .filter(Boolean);
}

function normalizeRoutes(rawRes) {
    // supports routesRes.data.routes or routesRes.routes
    const routes = rawRes?.data?.routes || rawRes?.routes || [];
    return Array.isArray(routes) ? routes : [];
}

function isWorking(item) {
    return item && !item.dayOff;
}

export default function ViewSchedulePage() {
    const [viewMode, setViewMode] = useState("month"); // "week" | "month"
    const [selectedDate, setSelectedDate] = useState(new Date());

    const [courierOptions, setCourierOptions] = useState([]);
    const [routes, setRoutes] = useState([]);

    const [selectedCourier, setSelectedCourier] = useState(null); // {value,label} (value = courierId)
    const [selectedRouteName, setSelectedRouteName] = useState(""); // routeName
    const [statusFilter, setStatusFilter] = useState("all"); // all | working | not_working

    const [scheduleByDate, setScheduleByDate] = useState({}); // { "YYYY-MM-DD": [items...] }
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [loadingSchedule, setLoadingSchedule] = useState(true);
    const [err, setErr] = useState("");

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [modalDateISO, setModalDateISO] = useState("");

    const range = useMemo(() => {
        if (viewMode === "week") {
            const start = startOfWeekMonday(selectedDate);
            const end = endOfWeekMonday(selectedDate);
            return { start, end };
        }
        return { start: startOfMonth(selectedDate), end: endOfMonth(selectedDate) };
    }, [viewMode, selectedDate]);

    const rangeTitle = useMemo(() => formatRangeTitle(viewMode, selectedDate), [viewMode, selectedDate]);

    // Month grid needs leading blanks to align weekdays (Mon..Sun)
    const monthGrid = useMemo(() => {
        if (viewMode !== "month") return null;

        const start = startOfMonth(selectedDate);
        const end = endOfMonth(selectedDate);

        // Determine weekday index with Monday=0..Sunday=6
        const weekday = (d) => {
            const w = d.getDay(); // 0..6 (Sun..Sat)
            return w === 0 ? 6 : w - 1; // Mon=0 ... Sun=6
        };

        const leading = weekday(start); // number of blanks before day 1
        const days = getISODateRangeInclusive(start, end);
        return { leading, days };
    }, [viewMode, selectedDate]);

    const weekDaysISO = useMemo(() => {
        if (viewMode !== "week") return [];
        return getISODateRangeInclusive(range.start, range.end);
    }, [viewMode, range.start, range.end]);

    // Load couriers + routes (once)
    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoadingMeta(true);
            setErr("");
            try {
                const [couriersRes, routesRes] = await Promise.all([fetchCouriersBySearch(), fetchAllRoutes(1, 999999)]);
                if (!mounted) return;

                setCourierOptions(normalizeCourierOptions(couriersRes || []));
                if (routesRes?.success) {
                    setRoutes(normalizeRoutes(routesRes));
                } else {
                    setRoutes(normalizeRoutes(routesRes));
                }
            } catch (e) {
                if (!mounted) return;
                setErr(e?.message || "Failed to load couriers/routes");
            } finally {
                if (!mounted) return;
                setLoadingMeta(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // Fetch schedule for current range
    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoadingSchedule(true);
            setErr("");
            try {
                const res = await getCalendarSchedule({
                    start: toISODateLocal(range.start),
                    end: toISODateLocal(range.end),
                });

                if (!mounted) return;

                if (res?.success) setScheduleByDate(res.data || {});
                else setScheduleByDate({});
            } catch (e) {
                if (!mounted) return;
                setErr(e?.message || "Failed to fetch schedule");
                setScheduleByDate({});
            } finally {
                if (!mounted) return;
                setLoadingSchedule(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [range.start, range.end]);

    function matchesFilters(item) {
        // item fields: courierId, courierName, routeName, startTime, dayOff
        if (selectedCourier?.value && String(item.courierId) !== String(selectedCourier.value)) return false;
        if (selectedRouteName && String(item.routeName || "") !== String(selectedRouteName)) return false;

        if (statusFilter === "working") return isWorking(item);
        if (statusFilter === "not_working") return !isWorking(item);

        return true;
    }

    function dayWorkingCount(items) {
        return (items || []).filter((x) => matchesFilters(x) && isWorking(x)).length;
    }

    function dayVisibleWorking(items) {
        return (items || []).filter((x) => matchesFilters(x) && isWorking(x));
    }

    function dayVisibleAll(items) {
        return (items || []).filter((x) => matchesFilters(x));
    }

    function handlePrev() {
        setSelectedDate((d) => (viewMode === "week" ? addDays(d, -7) : addMonths(d, -1)));
    }

    function handleNext() {
        setSelectedDate((d) => (viewMode === "week" ? addDays(d, 7) : addMonths(d, 1)));
    }

    function handleToday() {
        setSelectedDate(new Date());
    }

    function openDayModal(iso) {
        setModalDateISO(iso);
        onOpen();
    }

    const modalItemsAll = useMemo(() => {
        if (!modalDateISO) return [];
        return dayVisibleAll(scheduleByDate[modalDateISO] || []);
    }, [modalDateISO, scheduleByDate, selectedCourier, selectedRouteName, statusFilter]);

    const modalDateLabel = useMemo(() => {
        if (!modalDateISO) return "";
        return parseISOToLocalDate(modalDateISO).toDateString();
    }, [modalDateISO]);

    const courierSelectLoadOptions = (inputValue, callback) => {
        const q = String(inputValue || "").toLowerCase();
        const filtered = courierOptions.filter((o) => o.label.toLowerCase().includes(q));
        callback(filtered);
    };

    const isBusy = loadingMeta || loadingSchedule;

    return (
        <Box p={6}>
            <HStack justify="space-between" align="start" mb={4} wrap="wrap" gap={3}>
                <Box>
                    <Heading size="lg">Schedules</Heading>
                    <Text color="gray.500" mt={1}>
                        {rangeTitle}
                    </Text>
                </Box>

                <HStack>
                    <IconButton aria-label="Previous" icon={<ChevronLeftIcon />} onClick={handlePrev} />
                    <IconButton aria-label="Next" icon={<ChevronRightIcon />} onClick={handleNext} />
                    <Button variant="outline" onClick={handleToday}>
                        Today
                    </Button>
                </HStack>
            </HStack>

            {/* Filters / Controls */}
            <Box borderWidth="1px" borderRadius="xl" p={4} mb={6}>
                <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between" wrap="wrap" gap={3}>
                        <HStack wrap="wrap" gap={3}>
                            <ButtonGroup isAttached>
                                <Button
                                    onClick={() => setViewMode("week")}
                                    variant={viewMode === "week" ? "solid" : "outline"}
                                    colorScheme={viewMode === "week" ? "blue" : "gray"}
                                >
                                    Week
                                </Button>
                                <Button
                                    onClick={() => setViewMode("month")}
                                    variant={viewMode === "month" ? "solid" : "outline"}
                                    colorScheme={viewMode === "month" ? "blue" : "gray"}
                                >
                                    Month
                                </Button>
                            </ButtonGroup>

                            <Select
                                maxW="220px"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                title="Status"
                            >
                                <option value="all">All</option>
                                <option value="working">Working</option>
                                <option value="not_working">Not working</option>
                            </Select>
                        </HStack>

                        <HStack wrap="wrap" gap={3}>
                            <Box minW="260px">
                                <AsyncSelect
                                    cacheOptions
                                    loadOptions={courierSelectLoadOptions}
                                    defaultOptions={courierOptions}
                                    value={selectedCourier}
                                    onChange={setSelectedCourier}
                                    placeholder="Filter by courier"
                                    isClearable
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: 40 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                />
                            </Box>

                            <Select
                                placeholder="All routes"
                                maxW="220px"
                                value={selectedRouteName}
                                onChange={(e) => setSelectedRouteName(e.target.value)}
                            >
                                {routes.map((r) => (
                                    <option key={r._id} value={r.name}>
                                        {r.name}
                                    </option>
                                ))}
                            </Select>

                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setSelectedCourier(null);
                                    setSelectedRouteName("");
                                    setStatusFilter("all");
                                }}
                            >
                                Clear filters
                            </Button>
                        </HStack>
                    </HStack>

                    {err ? (
                        <Box borderWidth="1px" borderRadius="lg" p={3} borderColor="red.200">
                            <Text color="red.600" fontSize="sm">
                                {err}
                            </Text>
                        </Box>
                    ) : null}
                </VStack>
            </Box>

            {isBusy ? (
                <Box py={14} textAlign="center">
                    <Spinner size="lg" />
                </Box>
            ) : viewMode === "week" ? (
                // WEEK VIEW (table, easy to read)
                <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th w="200px">Day</Th>
                                <Th>Working</Th>
                                <Th>Details</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {weekDaysISO.map((iso) => {
                                const items = scheduleByDate[iso] || [];
                                const working = dayVisibleWorking(items);
                                const count = working.length;

                                return (
                                    <Tr key={iso}>
                                        <Td>
                                            <Text fontWeight="semibold">{parseISOToLocalDate(iso).toDateString()}</Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {iso}
                                            </Text>
                                        </Td>

                                        <Td>
                                            {count === 0 ? (
                                                <Text color="gray.500" fontSize="sm">
                                                    No one working
                                                </Text>
                                            ) : (
                                                <Wrap spacing={2}>
                                                    {working.slice(0, 6).map((x, idx) => (
                                                        <WrapItem key={`${x.courierId}-${idx}`}>
                                                            <Badge borderRadius="full" px={2} py={1} colorScheme="blue">
                                                                {x.courierName} {x.startTime ? `• ${x.startTime}` : ""}{" "}
                                                                {x.routeName ? `• ${x.routeName}` : ""}
                                                            </Badge>
                                                        </WrapItem>
                                                    ))}
                                                    {count > 6 ? (
                                                        <WrapItem>
                                                            <Badge borderRadius="full" px={2} py={1}>
                                                                +{count - 6} more
                                                            </Badge>
                                                        </WrapItem>
                                                    ) : null}
                                                </Wrap>
                                            )}
                                        </Td>

                                        <Td>
                                            <Button size="sm" variant="outline" onClick={() => openDayModal(iso)}>
                                                View day
                                            </Button>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            ) : (
                // MONTH VIEW (calendar grid)
                <Box borderWidth="1px" borderRadius="xl" p={4}>
                    <SimpleGrid columns={7} spacing={3} mb={3}>
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                            <Text key={d} fontSize="sm" fontWeight="semibold" color="gray.500" textAlign="center">
                                {d}
                            </Text>
                        ))}
                    </SimpleGrid>

                    <SimpleGrid columns={7} spacing={3}>
                        {Array.from({ length: monthGrid?.leading || 0 }).map((_, i) => (
                            <Box key={`blank-${i}`} />
                        ))}

                        {(monthGrid?.days || []).map((iso) => {
                            const items = scheduleByDate[iso] || [];
                            const working = dayVisibleWorking(items);
                            const workingCount = working.length;

                            // Decide if this day should render based on status filter:
                            if (statusFilter === "working" && workingCount === 0) return null;
                            if (statusFilter === "not_working" && workingCount > 0) return null;

                            const d = parseISOToLocalDate(iso);
                            const dayNum = d.getDate();

                            return (
                                <Box
                                    key={iso}
                                    borderWidth="1px"
                                    borderRadius="lg"
                                    p={3}
                                    minH="120px"
                                    onClick={() => openDayModal(iso)}
                                    cursor="pointer"
                                    _hover={{ borderColor: "blue.300" }}
                                >
                                    <HStack justify="space-between" mb={2}>
                                        <Text fontWeight="bold">{dayNum}</Text>
                                        <Badge variant="subtle" colorScheme={workingCount ? "green" : "gray"}>
                                            {workingCount ? `${workingCount} working` : "off"}
                                        </Badge>
                                    </HStack>

                                    {workingCount === 0 ? (
                                        <Text fontSize="sm" color="gray.500">
                                            No one working
                                        </Text>
                                    ) : (
                                        <VStack align="start" spacing={1}>
                                            {working.slice(0, 3).map((x, idx) => (
                                                <Text key={`${x.courierId}-${idx}`} fontSize="xs" noOfLines={1}>
                                                    {x.courierName}
                                                    {x.startTime ? ` • ${x.startTime}` : ""}
                                                    {x.routeName ? ` • ${x.routeName}` : ""}
                                                </Text>
                                            ))}
                                            {workingCount > 3 ? (
                                                <Text fontSize="xs" color="blue.600">
                                                    +{workingCount - 3} more
                                                </Text>
                                            ) : null}
                                        </VStack>
                                    )}
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                </Box>
            )}

            {/* Day Details Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{modalDateISO ? `Schedule: ${modalDateLabel}` : "Schedule"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={5}>
                        {!modalDateISO ? null : (
                            <Stack spacing={4}>
                                <Box>
                                    <Text fontSize="sm" color="gray.500">
                                        Showing results based on current filters.
                                    </Text>
                                </Box>

                                <Divider />

                                {modalItemsAll.length === 0 ? (
                                    <Text color="gray.500">No matching entries.</Text>
                                ) : (
                                    <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                                        <Table size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th>Courier</Th>
                                                    <Th>Start</Th>
                                                    <Th>Route</Th>
                                                    <Th>Status</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {modalItemsAll.map((x, idx) => (
                                                    <Tr key={`${x.courierId}-${idx}`}>
                                                        <Td>{x.courierName || "—"}</Td>
                                                        <Td>{x.startTime || "—"}</Td>
                                                        <Td>{x.routeName || "—"}</Td>
                                                        <Td>
                                                            {x.dayOff ? (
                                                                <Badge colorScheme="gray">Off</Badge>
                                                            ) : (
                                                                <Badge colorScheme="green">Working</Badge>
                                                            )}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}